import { useEffect, useState } from 'react';
import { CheckCircleOutlined, CloudUploadOutlined, KeyOutlined, LinkOutlined, MessageOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, Popconfirm, Switch, Table, Tag } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';

const FIELDS = ['enabled', 'apiBase', 'publicBaseUrl', 'uploadApiUrl'];
const KIND_LABEL = {
    invoice: 'Invoice',
    reminder: 'Reminder',
    statement: 'Statement',
    report: 'Report',
    gst: 'GST',
    test: 'Test',
    other: 'Other'
};

/**
 * Settings › WhatsApp: the WhatsApp Web V2 key and the OneSaas upload key. The desktop never talks to either
 * provider. Connection status and every send are stored here.
 */
export function WhatsAppSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [testingUpload, setTestingUpload] = useState(false);
    const [uploadTest, setUploadTest] = useState(null);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/whatsapp').then((body) => body.settings));
    const sessions = useLoad(() => api('/v1/admin/whatsapp/sessions').then((body) => body.sessions ?? []));
    const reports = useLoad(() => api('/v1/admin/whatsapp/messages').then((body) => body.messages ?? []));
    const settings = data;
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ ...Object.fromEntries(FIELDS.map((key) => [key, settings[key]])), apiKey: '', uploadApiKey: '' });
        setDirty(false);
    }, [settings, form]);
    const values = () => {
        const all = form.getFieldsValue();
        return { ...Object.fromEntries(FIELDS.map((key) => [key, all[key]])), apiKey: all.apiKey ?? '', uploadApiKey: all.uploadApiKey ?? '' };
    };
    async function save(clearKey = false, clearUploadKey = false) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/settings/whatsapp', { method: 'PUT', body: JSON.stringify({ ...values(), clearKey, clearUploadKey, ...(clearKey ? { enabled: false } : {}) }) });
            message.success(clearKey ? 'Key removed. WhatsApp is off.' : clearUploadKey ? 'Upload key removed. Invoice attachments need a new key.' : body.settings.active ? 'Saved. Desktop WhatsApp goes through this server.' : 'Saved. WhatsApp is off.');
            setTest(null);
            setUploadTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function sendTest() {
        setTesting(true);
        setTest(null);
        try {
            const result = await api('/v1/admin/settings/whatsapp/test', { method: 'POST', body: JSON.stringify(values()) });
            setTest({ ok: true, text: `WhatsApp service answered: ${result.health?.status ?? 'ok'}.` });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The WhatsApp service could not be reached' });
        }
        finally {
            setTesting(false);
        }
    }
    async function sendUploadTest() {
        setTestingUpload(true);
        setUploadTest(null);
        try {
            const result = await api('/v1/admin/settings/whatsapp/test-upload', { method: 'POST', body: JSON.stringify(values()) });
            setUploadTest({ ok: true, text: result.url ? `Upload answered. File URL: ${result.url}${result.ttlHours ? ` · expires in ${result.ttlHours} hours` : ''}` : 'Upload answered.' });
        }
        catch (err) {
            setUploadTest({ ok: false, text: err instanceof Error ? err.message : 'The file upload service could not be reached' });
        }
        finally {
            setTestingUpload(false);
        }
    }
    const on = Boolean(settings?.active);
    return (
        <>
            <PageHeader title="WhatsApp" description="The desktop app connects a phone by QR and sends invoices, payment reminders and statements through this server. The API key stays here; Kivonex Cloud talks to WhatsApp Web V2." actions={<BackToSettings />} />
            <ErrorNotice error={error} />
            {settings?.keyUnreadable && (
                <Alert type="warning" showIcon title="The saved WhatsApp key cannot be read on this server" description="The server's settings key was replaced or lost, so WhatsApp is off until the key is entered again." style={{ marginBottom: 20 }} />
            )}
            {settings?.uploadKeyUnreadable && (
                <Alert type="warning" showIcon title="The saved file upload key cannot be read on this server" description="Invoice and statement attachments will fail until the upload key is entered again." style={{ marginBottom: 20 }} />
            )}
            {settings && settings.enabled && !on && (
                <Alert type="warning" showIcon title="WhatsApp is switched on but not complete" description={settings.missing} style={{ marginBottom: 20 }} />
            )}

            <div className="stats">
                <StatCard label="WhatsApp" tone={on ? 'green' : 'slate'} icon={<WhatsAppOutlined />} loading={loading && !data} value={on ? 'On' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'} />
                <StatCard label="Linked companies" tone="indigo" icon={<LinkOutlined />} loading={sessions.loading && !sessions.data} value={sessions.data?.filter((row) => row.linked).length ?? 0} foot={`${sessions.data?.length ?? 0} session${(sessions.data?.length ?? 0) === 1 ? '' : 's'}`} />
                <StatCard label="Messages logged" tone="amber" icon={<MessageOutlined />} loading={reports.loading && !reports.data} value={reports.data?.length ?? 0} foot="Latest 100 on this page" />
            </div>

            {loading && !data ? (
                <Panel flush title="WhatsApp Web V2" description="whatsappweb.onesaas.in">
                    <FormSkeleton sections={3} />
                </Panel>
            ) : (
                <Form form={form} layout="vertical" requiredMark={false} onFinish={() => void save()} onValuesChange={() => setDirty(true)}>
                    <Panel flush title="WhatsApp Web V2" description="whatsappweb.onesaas.in" extra={on ? <Tag color="success" variant="filled"><span className="status-dot" />On</Tag> : <Tag variant="filled">Off</Tag>}>
                        <div className="form-section">
                            <div>
                                <h4>Use WhatsApp through Kivonex Cloud</h4>
                                <p>While this is off, the desktop app cannot connect a phone or send messages. Switch it on after a health check succeeds.</p>
                            </div>
                            <div>
                                <Form.Item name="enabled" valuePropName="checked" label="WhatsApp on">
                                    <Switch />
                                </Form.Item>
                            </div>
                        </div>

                        <div className="form-section">
                            <div>
                                <h4>API key</h4>
                                <p>From the WhatsApp Web V2 project. Stored encrypted and never shown again. Sent as <span className="mono">x-api-key</span> from this server only.</p>
                            </div>
                            <div>
                                <Form.Item label="API key" name="apiKey" extra={settings?.apiKeySet ? <span><KeyOutlined style={{ color: 'var(--brand)' }} /> A key ending <span className="mono">{settings.apiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Without a key, nothing can be sent.'}>
                                    <Input.Password autoComplete="new-password" placeholder={settings?.apiKeySet ? '••••••••••••' : 'WhatsApp API key'} className="mono" />
                                </Form.Item>
                                {settings?.apiKeySet && (
                                    <Popconfirm title="Remove the saved key?" description="Desktop WhatsApp stops until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(true)}>
                                        <Button danger type="link" style={{ paddingInline: 0 }}>Remove key</Button>
                                    </Popconfirm>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div>
                                <h4>File upload (invoices and statements)</h4>
                                <p>WhatsApp only accepts a public http(s) URL. This server posts the PDF to upload.onesaas.in (header <span className="mono">key</span>, field <span className="mono">file</span>) and sends the returned URL. The key stays here; the desktop never calls the upload API.</p>
                            </div>
                            <div>
                                <Form.Item label="Upload API" name="uploadApiUrl" extra="Default https://upload.onesaas.in/api/upload">
                                    <Input className="mono" placeholder="https://upload.onesaas.in/api/upload" />
                                </Form.Item>
                                <Form.Item label="Upload API key" name="uploadApiKey" extra={settings?.uploadApiKeySet ? <span><KeyOutlined style={{ color: 'var(--brand)' }} /> A key ending <span className="mono">{settings.uploadApiKeyHint}</span> is saved. Leave blank to keep it.</span> : 'Without this key, invoice PDFs cannot be attached unless a public media URL is set below.'}>
                                    <Input.Password autoComplete="new-password" placeholder={settings?.uploadApiKeySet ? '••••••••••••' : 'Upload API key'} className="mono" />
                                </Form.Item>
                                {settings?.uploadApiKeySet && (
                                    <Popconfirm title="Remove the upload key?" description="Invoice and statement attachments stop until a key is added again." okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => save(false, true)}>
                                        <Button danger type="link" style={{ paddingInline: 0 }}>Remove upload key</Button>
                                    </Popconfirm>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <div>
                                <h4>Endpoints</h4>
                                <p>Leave the WhatsApp API base as the hosted service unless you run your own. Public media URL is only a fallback if the upload key above is empty.</p>
                            </div>
                            <div>
                                <Form.Item label="WhatsApp API base" name="apiBase" extra="Default https://whatsappweb.onesaas.in">
                                    <Input className="mono" placeholder="https://whatsappweb.onesaas.in" />
                                </Form.Item>
                                <Form.Item label="Public media URL (fallback)" name="publicBaseUrl" extra="Optional. This Cloud API's public origin, for example https://api.kivonex.in. Used only when the upload key is not set. Localhost is rejected by WhatsApp.">
                                    <Input className="mono" placeholder="https://api.kivonex.in" />
                                </Form.Item>
                            </div>
                        </div>

                        {(test || uploadTest) && (
                            <div style={{ padding: '0 20px 20px', display: 'grid', gap: 8 }}>
                                {test && <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text} />}
                                {uploadTest && <Alert type={uploadTest.ok ? 'success' : 'error'} showIcon title={uploadTest.text} />}
                            </div>
                        )}
                        <div className="form-actions">
                            <Button icon={<CheckCircleOutlined />} onClick={() => void sendTest()} loading={testing}>Check WhatsApp</Button>
                            <Button icon={<CloudUploadOutlined />} onClick={() => void sendUploadTest()} loading={testingUpload}>Check upload</Button>
                            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>Save changes</Button>
                        </div>
                    </Panel>
                </Form>
            )}

            <Panel title="Linked companies" description="Each company on a Kivonex account may link one phone. Status is stored here; a live check only runs when the user taps Check connection.">
                <ErrorNotice error={sessions.error} />
                <Table
                    size="small"
                    rowKey="id"
                    loading={sessions.loading}
                    dataSource={sessions.data ?? []}
                    pagination={false}
                    locale={{ emptyText: 'No company has connected WhatsApp yet.' }}
                    columns={[
                        { title: 'Company', dataIndex: 'companyId', render: (value) => <span className="mono">{value}</span> },
                        { title: 'Session', dataIndex: 'sessionId', render: (value) => value ? <span className="mono">{value}</span> : '—' },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            render: (value, row) => <Tag color={row.linked ? 'success' : value === 'linking' ? 'processing' : 'default'}>{row.linked ? 'Linked' : value}</Tag>
                        },
                        { title: 'Phone', render: (_value, row) => row.user?.id || '—' },
                        { title: 'Updated', dataIndex: 'updatedAt', render: (value) => value ? timeAgo(value) : '—' }
                    ]}
                />
            </Panel>

            <Panel title="Message reports" description="Every invoice, reminder, statement, report or test sent through Kivonex Cloud.">
                <ErrorNotice error={reports.error} />
                <Table
                    size="small"
                    rowKey="id"
                    loading={reports.loading}
                    dataSource={reports.data ?? []}
                    pagination={false}
                    locale={{ emptyText: 'No WhatsApp messages have been sent yet.' }}
                    columns={[
                        { title: 'When', dataIndex: 'createdAt', width: 140, render: (value) => value ? timeAgo(value) : '—' },
                        { title: 'Company', dataIndex: 'companyId', render: (value) => <span className="mono">{value}</span> },
                        { title: 'Kind', dataIndex: 'kind', width: 110, render: (value) => KIND_LABEL[value] ?? value },
                        { title: 'To', dataIndex: 'phone', render: (value) => <span className="mono">{value}</span> },
                        { title: 'Preview', dataIndex: 'preview', ellipsis: true },
                        {
                            title: 'Result',
                            dataIndex: 'status',
                            width: 110,
                            render: (value, row) => <Tag color={value === 'sent' ? 'success' : 'error'} title={row.errorMessage || undefined}>{value === 'sent' ? 'Sent' : row.errorCode || 'Failed'}</Tag>
                        }
                    ]}
                />
            </Panel>
        </>
    );
}
